import { MedplumClient } from '@medplum/core';
import { MedplumProvider } from '@medplum/react';
import '@medplum/react/styles.css';
import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import '@mantine/core/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/dates/styles.css';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { config } from './config';
import { cssVariablesResolver, theme } from './config/theme';
import { PermissionProvider } from './contexts/PermissionContext';
import { PortalSubjectProvider } from './contexts/PortalSubjectContext';
import { MessagingRealtimeProvider } from './pages/messaging/hooks/useMessagingRealtime';
import { clearStaleMedplumSessionIfNeeded } from './lib/clearStaleMedplumSession';
import { stampUnifyWrites } from './lib/unifyMedplumClient';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

function createRealMedplumClient(): MedplumClient {
  const medplum = new MedplumClient({
    baseUrl: config.medplum.baseUrl,
    clientId: config.medplum.clientId,
    cacheTime: 60000,
    autoBatchTime: 100,
    onUnauthenticated: () => {
      const path = window.location.pathname;
      if (
        path.startsWith('/dashboard') ||
        path.startsWith('/client-portal') ||
        path.startsWith('/onboarding')
      ) {
        window.location.href = '/signin';
      }
    },
  });
  // Stamp canonical meta.profile on every FHIR write so strictMode validates them.
  return stampUnifyWrites(medplum);
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

async function renderApp(): Promise<void> {
  console.log('[Medplum] Using MedplumClient at', config.medplum.baseUrl);
  await clearStaleMedplumSessionIfNeeded(config.medplum.baseUrl, config.medplum.clientId);
  const medplum = createRealMedplumClient();
  await medplum.getInitPromise();

  try {
    await medplum.getProfileAsync();
  } catch {
    medplum.clear();
  }

  if (
    config.isDevelopment &&
    !medplum.getProfile() &&
    config.medplum.clientId &&
    !config.medplum.clientSecret
  ) {
    console.warn(
      '[Medplum] VITE_MEDPLUM_CLIENT_SECRET is unset — unauthenticated intake (client_credentials) will 401. Staff and client-portal intake still work when a user is signed in.',
    );
  }

  if (config.isDevelopment) {
    (window as unknown as { medplum: MedplumClient }).medplum = medplum;
  }

  createRoot(rootElement!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <MedplumProvider medplum={medplum}>
            <PermissionProvider>
              <PortalSubjectProvider>
                <MantineProvider theme={theme} cssVariablesResolver={cssVariablesResolver}>
                  <DatesProvider settings={{ firstDayOfWeek: 0, weekendDays: [0, 6] }}>
                    <Notifications position="bottom-right" autoClose={5000} />
                    <MessagingRealtimeProvider>
                      <App />
                    </MessagingRealtimeProvider>
                  </DatesProvider>
                </MantineProvider>
              </PortalSubjectProvider>
            </PermissionProvider>
          </MedplumProvider>
        </BrowserRouter>
        {config.isDevelopment ? <ReactQueryDevtools initialIsOpen={false} /> : null}
      </QueryClientProvider>
    </StrictMode>,
  );
}

renderApp();
